import { useEffect, useState } from 'react';
import { Dialog, Textarea, Button, Alert, Select } from '@dreamtree-org/twreact-ui';

/**
 * Confirm a review decision with a reason (and optionally an assignee).
 *   action: { key, label, variant, reasonRequired } | null (closed)
 *   choices: { label, options: [{ value, label }] } — an extra required pick (e.g. how to resolve)
 *   onConfirm({ reason, assignee, choice }) → Promise; a thrown error message is shown inline.
 */
export default function ReasonDialog({ action, subject, onClose, onConfirm, assignees, choices, reasonHint }) {
  const [reason, setReason] = useState('');
  const [assignee, setAssignee] = useState('');
  const [choice, setChoice] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason('');
    setAssignee('');
    setChoice('');
    setErr('');
  }, [action && action.key, subject]);

  if (!action) return null;
  const needsAssignee = !!assignees;
  const canSubmit = (!action.reasonRequired || reason.trim()) && (!needsAssignee || assignee) && (!choices || choice);

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      await onConfirm({ reason: reason.trim(), assignee, choice });
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={`${action.label}${subject ? ` · ${subject}` : ''}`}
      size="md"
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={action.variant === 'outline' ? 'primary' : action.variant} onClick={submit} loading={busy} disabled={!canSubmit}>
            {action.label}
          </Button>
        </div>
      )}
    >
      <div className="space-y-3">
        {err ? <Alert variant="error" title="Not saved">{err}</Alert> : null}
        {needsAssignee ? (
          <Select label="Assign to" options={assignees.map((a) => ({ value: a, label: a }))} value={assignee} onChange={(v) => setAssignee(v)} closeOnSelect required />
        ) : null}
        {choices ? (
          <Select label={choices.label} options={choices.options} value={choice} onChange={(v) => setChoice(v)} closeOnSelect required />
        ) : null}
        <Textarea
          label={action.reasonRequired ? 'Reason (required, recorded in the audit trail)' : 'Note (optional, recorded in the audit trail)'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          showCount
          helperText={reasonHint}
        />
      </div>
    </Dialog>
  );
}
